import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  CalendarClock, Download, HandHeart, Heart, Loader2, LogOut, Printer, Receipt, ShieldCheck,
  Sparkles, Target, TrendingUp, CreditCard,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RegularGiving } from "@/components/common/RegularGiving";
import { api, apiError } from "@/lib/api";
import { dateTime, money, shortDate } from "@/lib/format";

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground/80">{hint}</p>}
  </div>
);

const Card = ({ children, className = "", testId }) => (
  <div data-testid={testId} className={`panel p-6 ${className}`}>{children}</div>
);

export default function DonorPortal() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [me, setMe] = useState(null);
  const [giving, setGiving] = useState(null);
  const [busy, setBusy] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [pledge, setPledge] = useState({ amount: 5000, frequency: "one_off", programme_code: "", note: "" });
  const [pay, setPay] = useState(null);
  const [mandates, setMandates] = useState([]);
  const [receiptMode, setReceiptMode] = useState("per_gift");
  const [fundCode, setFundCode] = useState("");
  const [volunteer, setVolunteer] = useState({ skills: "", availability: "", location: "", note: "" });

  const load = useCallback(async () => {
    const [c, m, g] = await Promise.all([
      api.get("/donor/portal-config"), api.get("/donor/me"), api.get("/donor/giving"),
    ]);
    setCfg(c.data);
    setMe(m.data);
    setGiving(g.data);
    try {
      const p = await api.get("/payments/methods");
      setPay(p.data);
      setFundCode(p.data.default_fund_code || "");
      setPledge((prev) => ({ ...prev,
        amount: p.data.currency === "INR" ? prev.amount : Math.max(p.data.min_gift || 25, 25) }));
      const m = await api.get("/payments/mandates");
      setMandates(m.data.items || []);
    } catch { setPay({ configured: false }); }
  }, []);

  useEffect(() => {
    load().catch((e) => {
      if (e?.response?.status === 401) navigate("/donor/signin", { replace: true });
      else setCfg(false);
    });
  }, [load, navigate]);

  const signOut = async () => {
    await api.post("/donor/logout");
    navigate("/donor/signin", { replace: true });
  };

  const openReceipt = async (receiptNo) => {
    try {
      const res = await api.get(`/donor/receipts/${receiptNo.split("/").map(encodeURIComponent).join("/")}`);
      setReceipt(res.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitPledge = async () => {
    setBusy("pledge");
    try {
      const res = await api.post("/donor/pledge", pledge);
      toast.success(res.data.message);
      setPledge({ amount: 5000, frequency: "one_off", programme_code: "", note: "" });
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const payNow = async () => {
    setBusy("checkout");
    try {
      const res = await api.post("/payments/checkout", {
        amount: pledge.amount, fund_code: fundCode, programme_code: pledge.programme_code,
        note: pledge.note, origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
      setBusy("");
    }
  };

  const startRegular = async () => {
    setBusy("subscribe");
    try {
      const res = await api.post("/payments/subscribe", {
        amount: pledge.amount, frequency: pledge.frequency, fund_code: fundCode,
        programme_code: pledge.programme_code, receipt_mode: receiptMode,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
      setBusy("");
    }
  };

  const submitVolunteer = async () => {
    setBusy("vol");
    try {
      const res = await api.post("/donor/volunteer", volunteer);
      toast.success(res.data.message);
      setVolunteer({ skills: "", availability: "", location: "", note: "" });
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const joinProject = async (code) => {
    setBusy(code);
    try {
      const res = await api.post("/donor/interest", { programme_code: code, kind: "project" });
      toast.success(res.data.message);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!cfg || !me || !giving) {
    return <div className="grid min-h-screen place-items-center bg-background">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Opening your account…
      </div>
    </div>;
  }

  const c = cfg.content;
  const s = giving.summary;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-card/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            {cfg.logo_url
              ? <img src={cfg.logo_url} alt="" className="h-8 w-8 rounded-[9px] object-cover" />
              : <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
                  <Heart className="h-3.5 w-3.5 text-white" />
                </span>}
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-semibold text-foreground"
                   data-testid="portal-org-name">{cfg.organisation}</div>
              <div className="text-[10.5px] text-muted-foreground">Donor account · {me.name}</div>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={signOut} data-testid="portal-signout-button"
                  className="h-8 rounded-full border-border text-[11px]">
            <LogOut className="mr-1 h-3.5 w-3.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-8">
        <Card testId="portal-hero" className="relative overflow-hidden">
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[1.8px] text-muted-foreground">
              Thank you, {(me.name || "").split(" ")[0]}
            </div>
            <h1 className="mt-2 max-w-2xl font-display text-3xl font-bold leading-[1.1] text-foreground sm:text-4xl">
              {c.headline}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{c.intro}</p>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["Lifetime giving", money(s.lifetime, "INR", true), TrendingUp],                ["Gifts recorded", s.gifts, HandHeart],
                ["Receipts available", s.receipts, Receipt],
                ["Supporting since", s.first_gift ? shortDate(s.first_gift) : "—", Sparkles],
              ].map(([label, value, Icon]) => (
                <div key={label} data-testid={`portal-stat-${String(label).toLowerCase().replace(/\W+/g, "-")}`}
                     className="rounded-xl border border-border bg-muted/25 p-3 transition-colors duration-200 hover:border-primary/40">
                  <Icon className="h-3.5 w-3.5 text-primary" />
                  <div className="mt-2 text-[19px] font-bold leading-none text-foreground">{value}</div>
                  <div className="mt-1 text-[10.5px] uppercase tracking-wide text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Tabs defaultValue="giving">
          <TabsList>
            <TabsTrigger value="giving" data-testid="portal-tab-giving">My giving</TabsTrigger>
            <TabsTrigger value="impact" data-testid="portal-tab-impact">The difference made</TabsTrigger>
            {c.show_projects && (
              <TabsTrigger value="projects" data-testid="portal-tab-projects">Projects</TabsTrigger>
            )}
            {c.show_donate_cta && (
              <TabsTrigger value="give" data-testid="portal-tab-give">Give again</TabsTrigger>
            )}
            {c.show_volunteer_form && (
              <TabsTrigger value="volunteer" data-testid="portal-tab-volunteer">Volunteer</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="giving" className="mt-4 space-y-4">
            {giving.by_year.length > 0 && (
              <Card testId="portal-by-year">
                <h2 className="text-base font-semibold text-foreground md:text-lg">Your giving by year</h2>
                <div className="mt-4 h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={giving.by_year}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="year" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                             tickFormatter={(v) => money(v, "INR", true)} width={70} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))",
                        borderRadius: 12, fontSize: 12 }}
                        cursor={{ fill: "hsl(var(--muted))", fillOpacity: 0.35 }}
                        formatter={(v) => money(v, "INR")} />
                      <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={54} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}

            <Card testId="portal-giving-history">
              <h2 className="text-base font-semibold text-foreground md:text-lg">Every gift, and its receipt</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      {["Date", "Reference", "Fund", "Purpose", "Amount", "Status", "Receipt"].map((h) => (
                        <th key={h} className="px-2 py-2 text-[10px] uppercase tracking-wide text-muted-foreground">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {giving.items.length === 0 && (
                      <tr><td colSpan={7} className="px-2 py-6 text-center text-[12px] text-muted-foreground"
                              data-testid="portal-no-gifts">
                        No gifts recorded yet.</td></tr>
                    )}
                    {giving.items.map((g) => (
                      <tr key={g.id} className="row-hover" data-testid={`portal-gift-${g.reference}`}>
                        <td className="px-2 py-2.5 font-mono text-[11px]">{g.date}</td>
                        <td className="px-2 py-2.5 font-mono text-[11px] text-foreground">{g.reference}</td>
                        <td className="px-2 py-2.5 font-mono text-[11px]">{g.fund_code}</td>
                        <td className="px-2 py-2.5 text-[11.5px] text-muted-foreground">
                          {g.campaign || g.restriction}
                        </td>
                        <td className="px-2 py-2.5 font-mono text-[11.5px] text-foreground">
                          {money(g.amount, g.currency)}
                        </td>
                        <td className="px-2 py-2.5">
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] ${
                            g.status === "posted"
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                              : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}`}>
                            {g.status}
                          </span>
                        </td>
                        <td className="px-2 py-2.5">
                          {g.receipt_no
                            ? <Button size="sm" variant="outline" data-testid={`portal-receipt-${g.receipt_no}`}
                                      onClick={() => openReceipt(g.receipt_no)}
                                      className="h-7 rounded-full border-border px-2.5 text-[10px]">
                                <Download className="mr-1 h-3 w-3" /> {g.receipt_no}
                              </Button>
                            : <span className="text-[10.5px] text-muted-foreground">
                                {g.receipt_note || "pending"}
                              </span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="impact" className="mt-4 space-y-4">
            <Card testId="portal-impact">
              <h2 className="text-base font-semibold text-foreground md:text-lg">{c.story_title}</h2>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">{c.story_body}</p>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {(c.impact_stats || []).map((st, i) => (
                  <div key={i} data-testid={`portal-impact-stat-${i}`}
                       className="rounded-xl border border-border bg-muted/25 p-4">
                    <div className="font-display text-2xl font-bold text-foreground">{st.value}</div>
                    <div className="mt-1 text-[11.5px] text-muted-foreground">{st.label}</div>
                  </div>
                ))}
              </div>
              <p className="mt-6 max-w-3xl border-l-2 border-primary pl-3 text-[12.5px] italic leading-relaxed text-muted-foreground">
                {c.impact_statement}
              </p>
            </Card>

            {giving.by_fund.length > 0 && (
              <Card testId="portal-by-fund">
                <h2 className="text-base font-semibold text-foreground md:text-lg">Which funds your money went into</h2>
                <div className="mt-4 space-y-2.5">
                  {giving.by_fund.map((f) => {
                    const pct = Math.round((f.amount / (s.lifetime || 1)) * 100);
                    return (
                      <div key={f.fund_code} data-testid={`portal-fund-${f.fund_code}`}>
                        <div className="flex items-center justify-between text-[12px]">
                          <span className="font-mono text-foreground">{f.fund_code}</span>
                          <span className="font-mono text-muted-foreground">{money(f.amount, "INR", true)} · {pct}%</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-primary transition-all duration-700"
                               style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </TabsContent>

          {c.show_projects && (
            <TabsContent value="projects" className="mt-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {cfg.projects.length === 0 && (
                  <Card testId="portal-no-projects">
                    <p className="text-[12.5px] text-muted-foreground">No projects published yet.</p>
                  </Card>
                )}
                {cfg.projects.map((p) => (
                  <Card key={p.code} testId={`portal-project-${p.code}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[1.4px] text-muted-foreground">
                          {p.code} · {p.country}
                        </div>
                        <h3 className="mt-1 text-base font-semibold text-foreground">{p.name}</h3>
                      </div>
                      <Target className="h-4 w-4 shrink-0 text-primary" />
                    </div>
                    <p className="mt-2 text-[12.5px] leading-relaxed text-muted-foreground">{p.summary}</p>
                    <div className="mt-4 flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary transition-all duration-700"
                             style={{ width: `${Math.min(100, Math.round((p.spent / (p.budget || 1)) * 100))}%` }} />
                      </div>
                      <span className="font-mono text-[10.5px] text-muted-foreground">
                        {money(p.spent, "INR", true)} / {money(p.budget, "INR", true)}
                      </span>
                    </div>
                    <Button size="sm" onClick={() => joinProject(p.code)} disabled={busy === p.code}
                            data-testid={`portal-join-${p.code}`}
                            className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                      {busy === p.code && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                      Count me in
                    </Button>
                  </Card>
                ))}
              </div>
            </TabsContent>
          )}

          {c.show_donate_cta && (
            <TabsContent value="give" className="mt-4">
              <Card testId="portal-give">
                <h2 className="text-base font-semibold text-foreground md:text-lg">Give again</h2>
                <p className="mt-1 text-[12.5px] text-muted-foreground">
                  {pay?.configured
                    ? "Give by card or wallet in a few seconds — or tell the team and we will "
                      + "arrange it another way."
                    : c.donate_note}
                </p>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <F label={`Amount${pay?.currency ? ` (${pay.currency})` : ""}`}>
                    <Input type="number" value={pledge.amount} data-testid="portal-pledge-amount"
                           onChange={(e) => setPledge({ ...pledge, amount: Number(e.target.value) })}
                           className="bg-muted/40 font-mono" />
                  </F>
                  <F label="How often">
                    <Select value={pledge.frequency} onValueChange={(v) => setPledge({ ...pledge, frequency: v })}>
                      <SelectTrigger data-testid="portal-pledge-frequency" className="bg-muted/40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="one_off">One-off gift</SelectItem>
                        <SelectItem value="monthly">Every month</SelectItem>
                        <SelectItem value="quarterly">Every quarter</SelectItem>
                        <SelectItem value="annual">Once a year</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Towards">
                    <Select value={pledge.programme_code}
                            onValueChange={(v) => setPledge({ ...pledge, programme_code: v })}>
                      <SelectTrigger data-testid="portal-pledge-programme" className="bg-muted/40">
                        <SelectValue placeholder="Wherever needed most" /></SelectTrigger>
                      <SelectContent>{cfg.projects.map((p) => (
                        <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </F>
                  <div className="sm:col-span-3">
                    <F label="Anything we should know?">
                      <Textarea rows={2} value={pledge.note} data-testid="portal-pledge-note"
                                onChange={(e) => setPledge({ ...pledge, note: e.target.value })}
                                className="bg-muted/40 text-[12px]" />
                    </F>
                  </div>
                  {pay?.configured && (
                    <F label="Fund to credit">
                      <Select value={fundCode} onValueChange={setFundCode}>
                        <SelectTrigger data-testid="portal-pay-fund" className="bg-muted/40">
                          <SelectValue placeholder="Wherever needed most" /></SelectTrigger>
                        <SelectContent>{(pay.funds || []).map((f) => (
                          <SelectItem key={f.code} value={f.code}>{f.name}</SelectItem>))}</SelectContent>
                      </Select>
                    </F>
                  )}
                </div>
                {pay?.configured ? (
                  <div className="mt-5 rounded-xl border border-border bg-muted/20 p-4"
                       data-testid="portal-pay-panel">
                    <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-muted-foreground">
                      <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                      Card and wallet payments are taken by {pay.label} on their own secure page —
                      we never see your card. Charged in {pay.currency}.
                    </div>
                    {pledge.frequency === "one_off" ? (
                      <Button onClick={payNow} disabled={busy === "checkout" || !pledge.amount}
                              data-testid="portal-pay-now"
                              className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                        {busy === "checkout" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        <CreditCard className="mr-2 h-4 w-4" />
                        Give {pay.currency} {Number(pledge.amount || 0).toLocaleString()} now
                      </Button>
                    ) : (
                      <div className="mt-3 space-y-3">
                        <div className="max-w-xs">
                          <F label="Receipts" hint="You can change this with us at any time.">
                            <Select value={receiptMode} onValueChange={setReceiptMode}>
                              <SelectTrigger data-testid="portal-receipt-mode" className="bg-muted/40">
                                <SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="per_gift">A receipt for every gift</SelectItem>
                                <SelectItem value="annual">One receipt a year, all gifts together</SelectItem>
                              </SelectContent>
                            </Select>
                          </F>
                        </div>
                        <Button onClick={startRegular}
                                disabled={busy === "subscribe" || !pledge.amount}
                                data-testid="portal-subscribe"
                                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                          {busy === "subscribe" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          <CalendarClock className="mr-2 h-4 w-4" />
                          Give {pay.currency} {Number(pledge.amount || 0).toLocaleString()}{" "}
                          {pledge.frequency === "monthly" ? "every month"
                            : pledge.frequency === "quarterly" ? "every quarter" : "once a year"}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-5 rounded-xl border border-border bg-muted/20 p-4 text-[11.5px] text-muted-foreground"
                       data-testid="portal-pay-unavailable">
                    {pay?.message
                      || "Online card payments are not switched on yet — tell the team below and we "
                      + "will send you the payment options that suit you."}
                  </div>
                )}
                <Button onClick={submitPledge} disabled={busy === "pledge" || !pledge.amount}
                        data-testid="portal-pledge-submit"
                        variant="outline"
                        className="mt-4 rounded-full border-border">
                  {busy === "pledge" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <HandHeart className="mr-2 h-4 w-4" /> Tell the team instead
                </Button>

                {mandates.length > 0 && (
                  <div className="mt-6 border-t border-border pt-5">
                    <h3 className="text-[13px] font-semibold text-foreground">Your regular giving</h3>
                    <p className="mb-3 mt-1 text-[11.5px] text-muted-foreground">
                      Change the amount, pause it or stop it — it is yours to control.
                    </p>
                    <RegularGiving mandates={mandates} currency={pay?.currency || "GBP"}
                                   onChange={load} />
                  </div>
                )}
              </Card>
            </TabsContent>
          )}

          {c.show_volunteer_form && (
            <TabsContent value="volunteer" className="mt-4">
              <Card testId="portal-volunteer">
                <h2 className="text-base font-semibold text-foreground md:text-lg">Give your time as well</h2>
                <p className="mt-1 text-[12.5px] text-muted-foreground">
                  Tell us what you are good at. Our safeguarding team will start your clearance check —
                  you will not be placed with beneficiaries until it clears.
                </p>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <F label="Skills (comma separated)">
                    <Input value={volunteer.skills} data-testid="portal-volunteer-skills"
                           placeholder="Teaching, first aid, book-keeping"
                           onChange={(e) => setVolunteer({ ...volunteer, skills: e.target.value })}
                           className="bg-muted/40" />
                  </F>
                  <F label="Availability">
                    <Input value={volunteer.availability} data-testid="portal-volunteer-availability"
                           placeholder="Weekend mornings"
                           onChange={(e) => setVolunteer({ ...volunteer, availability: e.target.value })}
                           className="bg-muted/40" />
                  </F>
                  <F label="Where you can help">
                    <Input value={volunteer.location} data-testid="portal-volunteer-location"
                           onChange={(e) => setVolunteer({ ...volunteer, location: e.target.value })}
                           className="bg-muted/40" />
                  </F>
                  <F label="Anything else">
                    <Input value={volunteer.note} data-testid="portal-volunteer-note"
                           onChange={(e) => setVolunteer({ ...volunteer, note: e.target.value })}
                           className="bg-muted/40" />
                  </F>
                </div>
                <Button onClick={submitVolunteer}
                        disabled={busy === "vol" || volunteer.skills.trim().length < 3
                          || volunteer.availability.trim().length < 3}
                        data-testid="portal-volunteer-submit"
                        className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "vol" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign me up
                </Button>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      <Dialog open={Boolean(receipt)} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="max-w-2xl border-border bg-card" data-testid="portal-receipt-dialog"
                       aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Receipt {receipt?.receipt_no}</DialogTitle></DialogHeader>
          {receipt && (
            <>
              <div id="receipt-print-area"
                   className="max-h-[65vh] overflow-y-auto rounded-xl border border-border bg-white p-8 text-[#1c1f2e]">
                <div className="flex items-start justify-between border-b border-[#e3e5ee] pb-4">
                  <div>
                    <div className="text-[18px] font-bold">{receipt.organisation}</div>
                    <div className="mt-1 text-[11px] text-[#6b6f80]">
                      {(receipt.registration_refs || []).join(" · ")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[1.5px] text-[#6b6f80]">Official receipt</div>
                    <div className="font-mono text-[13px] font-bold">{receipt.receipt_no}</div>
                    <div className="text-[11px] text-[#6b6f80]">{receipt.regime_label}</div>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4 text-[12px]">
                  {[["Received from", receipt.donor_name], ["Gift date", receipt.donation_date],
                    ["Amount", money(receipt.amount, receipt.currency)], ["Fund", receipt.fund_code],
                    ["Restriction", receipt.restriction], ["Donation reference", receipt.donation_reference],
                    ["Issued by", receipt.issued_by], ["Issued at", dateTime(receipt.issued_at)]].map(([k, v]) => (
                    <div key={k}>
                      <div className="text-[10px] uppercase tracking-wide text-[#8a8fa3]">{k}</div>
                      <div className="font-medium">{String(v ?? "—")}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-lg bg-[#f4f5f9] p-3.5 text-[11.5px] leading-relaxed">
                  {receipt.statement}
                </div>
                <div className="mt-4 text-[10.5px] text-[#8a8fa3]">
                  Recognised by {receipt.authority}.
                  {receipt.status === "void" && (
                    <span className="ml-1 font-bold uppercase text-red-600">This receipt has been voided.</span>
                  )}
                </div>
              </div>
              <Button onClick={() => window.print()} data-testid="portal-receipt-print"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Printer className="mr-2 h-4 w-4" /> Print or save as PDF
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
